import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'

import { useAuthStore } from '@/features/auth/store'
import { unauthenticatedPath } from '@/features/auth/route-access'
import { initializeAnalytics } from '@/lib/analytics'
import { registerAuthTokenProvider } from '@/lib/api/auth-token'
import { createQueryClient } from '@/lib/query-client'
import { isWebViewSession } from '@/lib/webview-session'

import { createAppRouter } from './router'
import { RouteSpinner } from '@/components/ui/RouteSpinner'
import { ApiErrorToastHost } from '@/components/ui/ApiErrorToastHost'
import './styles.css'

const queryClient = createQueryClient()
const router = createAppRouter(queryClient)

const unsubscribeAnalytics = initializeAnalytics(router)
if (import.meta.hot) import.meta.hot.dispose(unsubscribeAnalytics)

// Let backend 401s, not local expiry checks, reject the token used by API requests.
registerAuthTokenProvider(
  () => useAuthStore.getState().session?.accessToken ?? null,
  () => {
    useAuthStore.getState().clearSession()
    if (isWebViewSession()) {
      void queryClient.cancelQueries()
      queryClient.clear()
    }
    void router.navigate({ to: unauthenticatedPath(), replace: true })
  },
)

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found.')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<RouteSpinner />}>
        <RouterProvider router={router} />
      </Suspense>
      <ApiErrorToastHost />
    </QueryClientProvider>
  </StrictMode>,
)
